// SCR-STAFF-1009 — اتصال شعبه به مرکز بیمه‌گر (US-INT-116؛ AP-INT-10): هر شعبه به یکی از مراکزی که پزشک واردشده نزد بیمه‌گر در
// آن عضو است (فهرست «محل فعالیت پزشک» — §۴.۱۲ قرارداد تامین) وصل می‌شود، یا «مطب» علامت می‌خورد و به‌جای سیام شناسه پزشک
// ارسال می‌شود. انتخاب مرکزی که کاربر در آن عضو نیست ← PHX-INT-0349 (BR-INT-112).
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { unwrap, type BranchInsurerSiteView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, FormDialog, JalaliDate, ListPage, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { WriteNotice } from '../settings/common';
import { intKeys } from './common';

export const SCR_STAFF_1009 = 'SCR-STAFF-1009';

/** بیمه‌گر این صفحه (تنها بیمه‌گر دارای اتصال رسمی در فاز ۳). */
const INSURER = 'TAMIN';

/** شعبه در نمای صفحه: اتصال ثبت‌شده یا شعبه‌ای که هنوز وصل نشده است. */
interface BranchRow {
  branchId: string;
  branchName: string;
  site: BranchInsurerSiteView | null;
}

function SiteDialog({
  row,
  onClose,
}: {
  row: BranchRow;
  onClose: () => void;
}) {
  const { t } = useTranslation('int');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [siamId, setSiamId] = useState(row.site?.siamId ?? '');
  const [isOffice, setIsOffice] = useState(row.site?.isOffice ?? false);

  // AC-INT-116.1: فهرست مراکز با توکن همان کاربر واردشده در بیمه‌گر گرفته می‌شود.
  const clinics = useQuery({
    queryKey: intKeys.insurerClinics(session?.tenantId),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/integration/branch-insurer-sites/clinics', { params: { query: { insurer: INSURER } } })),
  });

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/integration/branch-insurer-sites/{branchId}', {
          params: { path: { branchId: row.branchId }, query: { insurer: INSURER } },
          body: { siamId: isOffice ? null : siamId, isOffice, rowVersion: row.site?.rowVersion ?? 0 },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: intKeys.insurerSites(session?.tenantId) });
      onClose();
    },
  });

  return (
    <FormDialog
      open
      screenId={SCR_STAFF_1009}
      titleKey="int:insurer_sites.dialog_title"
      submitKey="common:actions.save"
      isDirty={siamId !== (row.site?.siamId ?? '') || isOffice !== (row.site?.isOffice ?? false)}
      isSubmitting={save.isPending}
      submitError={save.error}
      onClose={onClose}
      onSubmit={() => save.mutate()}
    >
      <Stack spacing={2}>
        <Typography variant="body2">{row.branchName}</Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={isOffice}
              onChange={(event) => setIsOffice(event.target.checked)}
              inputProps={{ 'aria-label': t('int:insurer_sites.is_office'), 'data-testid': 'site-is-office' } as never}
            />
          }
          label={t('int:insurer_sites.is_office')}
        />
        {isOffice ? (
          <Alert severity="info">{t('int:insurer_sites.office_hint')}</Alert>
        ) : (
          <TextField
            select
            label={t('int:insurer_sites.siam')}
            value={siamId}
            onChange={(event) => setSiamId(event.target.value)}
            disabled={clinics.isPending}
            slotProps={{ htmlInput: { 'data-testid': 'site-siam' } }}
          >
            {(clinics.data?.items ?? []).map((clinic) => (
              <MenuItem key={clinic.siamId} value={clinic.siamId}>
                {clinic.hospitalName}
                {t('common:list_separator')}
                <bdi dir="ltr">{clinic.siamId}</bdi>
              </MenuItem>
            ))}
          </TextField>
        )}
        {clinics.error ? <ErrorState error={clinics.error} compact /> : null}
      </Stack>
    </FormDialog>
  );
}

export function BranchInsurerSitesPage() {
  const { t } = useTranslation('int');
  const { tenantApi, session, activeTenant } = useTenantSession();
  const write = useWriteAccess('int.endpoint.manage');
  const [editing, setEditing] = useState<BranchRow | null>(null);

  const sites = useQuery({
    queryKey: intKeys.insurerSites(session?.tenantId),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/integration/branch-insurer-sites', { params: { query: { insurer: INSURER } } })),
  });

  const rows: BranchRow[] = (activeTenant?.branches ?? []).map((branch) => ({
    branchId: branch.branchId,
    branchName: branch.name ?? branch.branchId,
    site: sites.data?.items.find((item) => item.branchId === branch.branchId) ?? null,
  }));

  const columns: DataTableColumn<BranchRow>[] = [
    { id: 'branch', headerKey: 'int:insurer_sites.columns.branch', cell: (row) => row.branchName },
    {
      id: 'mode',
      headerKey: 'int:insurer_sites.columns.mode',
      cell: (row) => (
        <span data-testid={`site-mode-${row.branchId}`} data-mode={row.site ? (row.site.isOffice ? 'office' : 'siam') : 'none'}>
          <StatusChip
            tone={row.site ? 'active' : 'inactive'}
            labelKey={
              row.site
                ? row.site.isOffice
                  ? 'int:insurer_sites.mode.office'
                  : 'int:insurer_sites.mode.center'
                : 'int:insurer_sites.mode.unset'
            }
          />
        </span>
      ),
    },
    {
      id: 'siam',
      headerKey: 'int:insurer_sites.columns.siam',
      cell: (row) => (row.site?.siamId ? <bdi dir="ltr">{row.site.siamId}</bdi> : <span>—</span>),
    },
    { id: 'center', headerKey: 'int:insurer_sites.columns.center', cell: (row) => row.site?.centerName ?? '—' },
    {
      id: 'set_at',
      headerKey: 'int:insurer_sites.columns.set_at',
      cell: (row) => (row.site?.setAt ? <JalaliDate value={row.site.setAt} withTime /> : <span>—</span>),
    },
    {
      id: 'actions',
      headerKey: 'int:insurer_sites.columns.actions',
      cell: (row) => (
        <Tooltip title={t('int:insurer_sites.edit')}>
          <span>
            <IconButton
              aria-label={`${t('int:insurer_sites.edit')} ${row.branchName}`}
              onClick={() => setEditing(row)}
              disabled={!write.canWrite}
              data-testid={`site-edit-${row.branchId}`}
            >
              <EditOutlinedIcon />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_1009}
      titleKey="int:insurer_sites.title"
      subtitleKey="int:insurer_sites.subtitle"
      filters={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          <Alert severity="info">{t('int:insurer_sites.membership_hint')}</Alert>
        </Stack>
      }
    >
      <DataTable
        captionKey="int:insurer_sites.caption"
        columns={columns}
        rows={rows}
        getRowId={(row) => row.branchId}
        isLoading={sites.isPending}
        error={sites.error}
        onRetry={() => void sites.refetch()}
        emptyTitleKey="int:insurer_sites.empty"
      />
      {editing ? <SiteDialog row={editing} onClose={() => setEditing(null)} /> : null}
    </ListPage>
  );
}
